import React from 'react';
import _, { compact, filter, find, forEach, includes, isEmpty, isFunction, isString } from 'lodash';
import classnames from 'classnames';
import { Input, Checkbox, Button, Divider } from 'antd';
import {withErrorBoundary} from 'react-error-boundary';
import { Sorters } from 'utils/tableUtils';
import VirtualList from 'rc-virtual-list';
import { getInnerText } from 'components/Vocabulary';
import LoadingIcon from 'ac-components/ac-icons/LoadingIcon';
import { displayTextWidth } from 'utils/textUtil';
import ACMiddleEllipsis from 'ac-components/ac-middle-ellipsis/ACMiddleEllipsis';

class ColumnFilter extends React.Component{
    static defaultProps = {
        filterLabelFormatter: (label) => label,
        localSearch: true,
    }
    static getDerivedStateFromProps(props, state){
        const { searchText, optionSet1, optionSet2 } = state;
        const { filterLabelFormatter, localSearch } = props;
        const updatedState = {};
        const _searchText = searchText.toLowerCase();
        updatedState.matchedOptionSet1 = _searchText && localSearch ? filter(optionSet1, option => searchOption({option, searchText: _searchText, filterLabelFormatter})) : optionSet1;
        updatedState.matchedOptionSet2 = _searchText && localSearch ? filter(optionSet2, option => searchOption({option, searchText: _searchText, filterLabelFormatter})) : optionSet2;

        return updatedState;
    }
    inputRef = React.createRef()
    listContainerRef = React.createRef()
    constructor(props){
        super(props);
        const selected = isEmpty(props.selectedKeys) ? [] : [...props.selectedKeys];
        this.state = {
            searchText: '',
            selected,
            changed: false,
            maxListHeight: 400, // default max height
            maxListWidth: 220, // default width
            ...this.getOptionSets(selected)
        }
        _.bindAll(
            this,
            'searchChanged',
            'toogleValues',
            'applyFilter',
            'clearFilter',
            'selectAllMatched',
            'calculateMaxHeight',
            'calculateMaxWidth'
        )
    }
    searchChanged(e){
        this.setState({
            searchText: e.target.value
        });
    }
    focusInput(){
        if(this.inputRef.current){
            this.focusTimeOut = setTimeout(() => {
                this.inputRef.current.focus();
                this.calculateMaxHeight();
                this.calculateMaxWidth();
            }, 300);
        }
    }
    componentWillUnmount() {
        if (!_.isUndefined(this.focusTimeOut)) {
            clearTimeout(this.focusTimeOut);
        }
    }
    calculateMaxHeight(){
        if(this.listContainerRef.current){
            const rect = this.listContainerRef.current.getBoundingClientRect();
            const parentElement = this.listContainerRef.current.parentElement?.parentElement;
            const parentClassList = parentElement?.classList;
            const isTopAligned = parentClassList?.contains('ant-dropdown-placement-topLeft') || parentClassList?.contains('ant-dropdown-placement-topRight');
            const windowHeight = window.innerHeight;
            let availableSpace = windowHeight - rect.top - 24; // 24px offset from bottom
            if (isTopAligned) {
                availableSpace = windowHeight - (windowHeight - rect.bottom) - 24; // 24px offset from bottom
            }
            const bottomSpace = 60; // space for buttons and padding inside dropdown
            const maxHeight = Math.max(160, availableSpace - bottomSpace); // minimum 160px
            this.setState({ maxListHeight: maxHeight });
        }
    }
    calculateMaxWidth(){
        const { matchedOptionSet1, matchedOptionSet2 } = this.state;
        const allOptions = [...matchedOptionSet1, ...matchedOptionSet2];

        const checkboxWidth = 16; // checkbox width
        const checkboxMargin = 8; // margin between checkbox and text
        const padding = 32; // container padding (16px each side)
        const scrollbarWidth = 8; // potential scrollbar
            
        let maxContentWidth = 0;
        allOptions.forEach(filter => {
            const { text, value } = filter;
            const filterText = this.props.filterLabelFormatter(text, value);
            const width = displayTextWidth({ text: filterText });
            
            if(filter.secondaryText) {
                const secondaryWidth = displayTextWidth({ text: filter.secondaryText });
                maxContentWidth = Math.max(maxContentWidth, width + secondaryWidth);
            } else {
                maxContentWidth = Math.max(maxContentWidth, width);
            }
        });

        const totalWidth = checkboxWidth + checkboxMargin + maxContentWidth + padding + scrollbarWidth;
        const constrainedWidth = Math.max(220, Math.min(380, totalWidth));

        this.setState({ maxListWidth: constrainedWidth });
    }
    componentDidMount(){
        this.focusInput();
        // for dynamic filter list, fetch the options only when 1st time filter is opened
        if(this.props.onSearchChange){
            this.props.onSearchChange('');
        }
    }
    componentDidUpdate(prevProps, prevState){
        const { visible, selectedKeys, onSearchChange, refreshOnOpen, allowEmptySearch } = this.props;
        const { searchText } = this.state;
        if(prevProps.visible !== visible){
            if(!visible){
                this.setState({
                    searchText: '',
                    selected: [],
                    maxListHeight: 360 //reset maxListHeight in dropdown for fresh calculation in next render
                });
            }
            if(visible){
                this.setState({
                    ...this.getOptionSets(selectedKeys),
                    selected: [...selectedKeys],
                    changed: false,
                }, this.focusInput);
            }
        }
        if(visible && prevProps.filters !== this.props.filters){
            this.setState({
                ...this.getOptionSets(selectedKeys),
            }, this.focusInput);
        }
        // not fetching filters again if search is emptied, we already have options
        if((prevState.searchText?.trim() !== searchText?.trim() && isFunction(onSearchChange) && searchText?.trim().length > 0) || allowEmptySearch || (prevProps.visible !== visible && visible && refreshOnOpen)){
            onSearchChange(searchText);
        }
    }
    getOptionSets(selectedKeys){
        const props = this.props;
        const optionSet1 = [], optionSet2 = [];
        const { filters = [], nullFilterValue, nullFilterLabel, sortOptions = true } = props;
        let allOptions = filters.map(getTextValue).filter(({ value }) => !_.isNull(value) && !_.isUndefined(value));

        if(props.localSearch){
            // only add selected keys if local search is enabled.
            // local search is false where the options are controlled from outside
            // therefore we won't add any extra options 
            forEach(selectedKeys, key => {
                if(!find(allOptions, {value: key}) && !_.isNull(key) && !_.isUndefined(key) && key !== nullFilterValue){
                    allOptions.push({text: key, value: key});
                }
            })
        }

        if (sortOptions) {
            allOptions = allOptions.sort(isFunction(sortOptions) ? sortOptions : Sorters.STRING('text'));
        }
        
        if(props.hasOwnProperty('nullFilterValue') && !_.isUndefined(nullFilterValue)){
            allOptions.unshift({ text: nullFilterLabel ? nullFilterLabel : 'N/A', value: nullFilterValue });
        }
        forEach(allOptions, option => {
            if(includes(selectedKeys, option.value)){
                optionSet1.push(option);
            } else {
                optionSet2.push(option);
            }
        });
        return {
            optionSet1,
            optionSet2,
            allOptions,
        }
    }
    toogleValues(e){
        const key = e.target['data-value'];
        // const checked = e.target.checked;
        const {selected} = this.state;
        const index = selected.indexOf(key);
        if(index === -1){
            this.setState({
                selected: [...selected, key],
                changed: true,
            });
            // setSelectedKeys([...selectedKeys, key]);
        } else {
            selected.splice(index, 1);
            this.setState({
                selected: [...selected],
                changed: true,
            });
        }
    }
    selectAllMatched(){
        const { matchedOptionSet1, matchedOptionSet2 } = this.state;
        // For example we have [True, False]  options -> User selected: [True] When user clicks "All": Old code: Only processes matchedOptionSet2 = [False] & vice versa  
        // User selected: [True, False], then cleared, When user clicks "All": Old code: Only processes matchedOptionSet2 = []
        let selected = _.clone(this.state.selected);
        //Now with this we are selecting both matchedOptionSet1 & matchedOptionSet2
        [...matchedOptionSet1, ...matchedOptionSet2].forEach((filter) => {
            const { value } = filter;
            const index = selected.indexOf(value);
            if(index === -1){
                selected = [...selected, value];
            }
        });
        this.setState({
            selected,
            changed: true,
        });
    }
    applyFilter(){
        const {confirm, setSelectedKeys} = this.props;
        const {selected} = this.state;
        setSelectedKeys(_.isEmpty(selected) ? [] : [...selected]);
        confirm();
    }
    clearFilter(){
        this.setState({
            selected: [],
            changed: true,
        });
    }
    render(){
        const { loading, helpText } = this.props;
        const { searchText, selected, matchedOptionSet1, matchedOptionSet2, allOptions, maxListHeight, maxListWidth } = this.state;
        const isFilterAvailable = !isEmpty(allOptions);
        const optionsToRender = compact([...matchedOptionSet1, ...matchedOptionSet2]);
        const itemHeight = 28.43
        // Calculate height based on content, but don't exceed maxListHeight
        const contentHeight = itemHeight * optionsToRender.length;
        const virtualListHeight = Math.min(contentHeight, maxListHeight);
        const contentDisplayWidth = maxListWidth - 48; // subtract container padding and margins
        return (
            <div className="column-filter column-filter-width" style={{ width: maxListWidth }}>
                <div className="p-xs p-b-0 d-flex align-items-center">
                    <Input
                        placeholder="Search"
                        value={searchText}
                        onChange={this.searchChanged}
                        ref={this.inputRef}
                    />
                </div>
                {
                    helpText
                    ?
                    <div className="text-xsmall-default color-muted text-italic p-x-sm p-t-xs">{helpText}</div>
                    :
                    null
                }
                <div className="column-filter-list m-xs" ref={this.listContainerRef}>
                    {
                        !isFilterAvailable && !loading ? 'No filter available' : optionsToRender.length === 0 && !loading ? 'No match found' :
                            <VirtualList
                                data={optionsToRender}
                                height={virtualListHeight}
                                itemHeight={itemHeight}
                                itemKey="id"
                            >
                                {((filter, i) => {
                                    const {text, value} = filter;
                                    const filterText = this.props.filterLabelFormatter(text, value);
                                    const labelClassName = classnames('flex-1 text-ellipsis', {'m-r-xs': filter.secondaryText});

                                    return (
                                        <div key={value}>
                                            <Checkbox 
                                                checked={selected.indexOf(value) > -1}
                                                data-value={value} 
                                                className={classnames({'m-b-xs': i < optionsToRender.length}, 'd-flex align-items-center')}
                                                onChange={this.toogleValues}
                                            >
                                                <div className="d-flex justify-content-between" style={{ width: contentDisplayWidth }}>
                                                    {
                                                        isString(filterText) ?
                                                            <ACMiddleEllipsis className={labelClassName} text={filterText} />
                                                            :
                                                            <div title={filterText} className={labelClassName}>{filterText}</div>
                                                    }
                                                    {filter.secondaryText && <div>{filter.secondaryText}</div>}
                                                </div>
                                            </Checkbox>
                                        </div>
                                    )
                                })}
                            </VirtualList>
                    }
                    {loading && (
                        // overflow is added to prevent flickering issue
                        <div className="text-center font-size-24 overflow-hidden">
                            <LoadingIcon />
                        </div>
                    )}
                </div>
                <div className="ant-table-filter-dropdown-btns">
                    {/* <Button.Group> */}
                    <div>
                    {optionsToRender.length ? <><Button size="small" type="link" onClick={this.clearFilter}>Clear</Button>
                        <Divider type="vertical" className="m-0" />
                        <Button size="small" type="link" onClick={this.selectAllMatched}>All</Button></>
                        : ''}
                    </div>
                        <Button type="primary" size="small" onClick={this.applyFilter} disabled={!this.state.changed}>{/* <CheckIcon /> */}Apply</Button>
                    {/* </Button.Group> */}
                </div>
            </div>
        );
    }
}

export default withErrorBoundary(ColumnFilter, {
    onError: (e) => {
        console.log('Error in filters', e);
    },
    fallbackRender: () => <div className="p-sm">Filter not available</div>
});

export function getTextValue(filter){
    let { text = filter, value = filter } = filter;
    const { id, name, secondaryText } = filter;
    if(typeof id !== 'undefined' && typeof name !== 'undefined'){
        /**
         * Some times backend will send name and id pairs, 
         * we do not want to remap the list at all places
         */
        text = name;
        value = id;
    }
    return {
        text, value, secondaryText
    }
}


const searchOption = ({option, searchText, filterLabelFormatter}) => {
    const { text, value } = option;
    // replacing toLowerCase with lodash.toLower to handle Numeric values as well
    let _text = _.toLower(text);
    // not to be match with the backend value, just the displaying text
    const filterText = filterLabelFormatter(text, value);
    
    _text = getInnerText(filterText).toLowerCase();
    
    try{
        if (_text.indexOf(searchText) === -1) {
            return false;
        }
    }
    catch(e){
        console.error('Error searching options for column filter', e, option, searchText);
        return false
    }
    
    return true;
}